import React from 'react';
import ReactDOM from 'react-dom';

/**
 * Modern toast notification system
 * Usage: import toast from '../shared/Toast';
 *        toast.success('Done!');
 *        toast.error('Something went wrong');
 */

let toastContainer = null;
let toastId = 0;

function getContainer() {
    if (toastContainer) return toastContainer;
    toastContainer = document.createElement('div');
    toastContainer.id = 'beu-toast-container';
    toastContainer.style.cssText = `
        position: fixed;
        /* below the page header, so it never covers Add, the location
           switcher or the bell */
        top: calc(76px + env(safe-area-inset-top, 0px));
        right: 20px;
        /* above every panel and sheet (SlidePanel 100000, sheets 100002),
           so a toast fired while one is open can be seen */
        z-index: 100100;
        display: flex;
        flex-direction: column;
        gap: 10px;
        pointer-events: none;
    `;
    document.body.appendChild(toastContainer);
    return toastContainer;
}

function showToast(message, type = 'success', duration = 3500) {
    const container = getContainer();
    const id = ++toastId;

    const wrapper = document.createElement('div');
    wrapper.id = `beu-toast-${id}`;
    container.appendChild(wrapper);

    const removeToast = () => {
        const el = document.getElementById(`beu-toast-${id}`);
        if (el) {
            el.style.animation = 'beuToastOut 0.2s ease forwards';
            setTimeout(() => {
                if (el.parentNode) el.parentNode.removeChild(el);
            }, 250);
        }
    };

    const autoClose = setTimeout(removeToast, duration);

    const iconMap = {
        success: (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="9" fill="#007EA0" />
                <path d="M5.5 9.5L7.5 11.5L12.5 6.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
        error: (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="9" fill="#dc3545" />
                <path d="M6 6L12 12M12 6L6 12" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
        ),
        info: (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="9" cy="9" r="9" fill="#007EA0" />
                <path d="M9 8V13" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="9" cy="5.5" r="1" fill="#fff" />
            </svg>
        ),
    };

    const toast = (
        <div
            style={{
                pointerEvents: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 18px',
                background: '#fff',
                borderRadius: '12px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.10), 0 1px 4px rgba(0, 0, 0, 0.06)',
                fontSize: '14px',
                fontWeight: '500',
                color: '#1a1a1a',
                minWidth: '260px',
                maxWidth: '380px',
                animation: 'beuToastIn 0.24s cubic-bezier(0.32, 0.72, 0, 1)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
        >
            <span style={{ flexShrink: 0, display: 'flex' }}>
                {iconMap[type] || iconMap.info}
            </span>
            <span style={{ flex: 1, lineHeight: '1.4' }}>{message}</span>
            <button
                onClick={() => {
                    clearTimeout(autoClose);
                    removeToast();
                }}
                style={{
                    flexShrink: 0,
                    background: 'none',
                    border: 'none',
                    padding: '2px',
                    cursor: 'pointer',
                    color: '#999',
                    fontSize: '16px',
                    lineHeight: 1,
                    display: 'flex',
                }}
            >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
            </button>
        </div>
    );

    ReactDOM.render(toast, wrapper);
}

// Inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('beu-toast-keyframes')) {
    const style = document.createElement('style');
    style.id = 'beu-toast-keyframes';
    style.textContent = `
        @keyframes beuToastIn {
            from { opacity: 0; transform: translateY(-8px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes beuToastOut {
            from { opacity: 1; transform: translateY(0) scale(1); }
            to { opacity: 0; transform: translateY(-8px) scale(0.98); }
        }
        @media (prefers-reduced-motion: reduce) {
            #beu-toast-container > div > div { animation: none !important; }
        }
        @media (max-width: 480px) {
            #beu-toast-container {
                top: auto !important;
                bottom: 20px !important;
                right: 12px !important;
                left: 12px !important;
            }
        }
    `;
    document.head.appendChild(style);
}

const toast = {
    success: (msg, duration) => showToast(msg, 'success', duration),
    error: (msg, duration) => showToast(msg, 'error', duration),
    info: (msg, duration) => showToast(msg, 'info', duration),
};

export { toast };
export default toast;
